import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef, useState } from 'react';

import { PORTFOLIO_QUERY_KEYS } from '@/api/portfolio/queries';
import { invalidateTransactionViews } from '@/api/transaction-views/cache-invalidation';

import { useVisibleWalletSync } from './use-visible-wallet-sync';
import { useWalletSyncRequest } from './use-wallet-sync-request';
import { useWalletSyncRunTracker } from './use-wallet-sync-run-tracker';

export const useWalletPortfolioSync = ({ enabled = true }: { enabled?: boolean } = {}) => {
  const queryClient = useQueryClient();
  const automaticSyncInFlight = useRef(false);
  const manualRequestCountRef = useRef(0);
  const [manualRequestCount, setManualRequestCount] = useState(0);
  const requestSync = useWalletSyncRequest();
  const { registerActiveRuns, hasManualRuns, walletSyncStatus } = useWalletSyncRunTracker({
    enabled,
  });

  const invalidateWalletViews = useCallback(
    () =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: [PORTFOLIO_QUERY_KEYS.walletPortfolio],
        }),
        queryClient.invalidateQueries({
          queryKey: [PORTFOLIO_QUERY_KEYS.walletSyncStatus],
        }),
        invalidateTransactionViews(queryClient),
      ]),
    [queryClient],
  );

  const synchronize = useCallback(async () => {
    const response = await requestSync();
    registerActiveRuns(response, manualRequestCountRef.current > 0);
    await invalidateWalletViews();
    return response;
  }, [invalidateWalletViews, registerActiveRuns, requestSync]);

  const requestAutomaticSync = useCallback(() => {
    if (automaticSyncInFlight.current) return;

    automaticSyncInFlight.current = true;
    void synchronize()
      .catch(() => undefined)
      .finally(() => {
        automaticSyncInFlight.current = false;
      });
  }, [synchronize]);

  useVisibleWalletSync({ enabled, requestSync: requestAutomaticSync });

  const refresh = useCallback(async () => {
    manualRequestCountRef.current += 1;
    setManualRequestCount(manualRequestCountRef.current);
    try {
      return await synchronize();
    } finally {
      manualRequestCountRef.current = Math.max(0, manualRequestCountRef.current - 1);
      setManualRequestCount(manualRequestCountRef.current);
    }
  }, [synchronize]);

  return {
    refresh,
    isRefreshing: manualRequestCount > 0 || hasManualRuns,
    walletSyncStatus,
  };
};
